import Highcharts, { Chart, ChartCallbackFunction } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { KpiTextForTracking, PerformanceType } from '@const/PerformanceType';
import { NoResult } from '@pages/NoResult';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, getPageTypeWithPageName, PageType } from '@utils/pageConfig/Types';
import { renderVisualization } from '../utilities/ChartUtils';
import { getChartOptions } from './Options';
import { ShareComparisonChartResp } from './Types';
import { PERFORMANCE_TREND_TITLE } from '@pages/Utilities';
import { IRootState, RetailerClientStateKeys } from '@state/reducers/Reducers';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { OnExportReadyCallback, useRegisterChartForExport } from '@ecosystem/export-utils';
import { EXPORT_CHART_SIZE, EXPORT_RENDERING_RESOLUTION } from '@hooks/export/Model';
import { useExportContext } from '@hooks/export/context/ExportContext';
import { isEmpty } from '@utils/ObjectUtils';
import { useSkipExportRenderingCallback } from '@components/design/migrated/DoubleBarChart/hooks/UseSkipExportRenderingCallback';
import ErrorResultMessage from '@pages/ErrorResultMessage';

const useShareComparisonChartCreateViewTrackingParam = (
  componentName: string,
  data: ShareComparisonChartResp,
  isValue: boolean,
  volume: string,
) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersStateRO = useSelector((state: any) => state.mainFiltersRO);
  const mainFiltersStateProducts = useSelector((state: any) => state.mainFiltersProducts);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const createParam: () => PageElementViewTrackingParam = () => {
    const effectiveProductType = getEffectiveProductType(subscribedProductTypes, retailerClientState.pageViewing);
    return {
      eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      componentName: componentName,
      elementType: ElementType.CHART,
      chartType: ElementType.COMBINED_CHART,
      elementKpi: isValue
        ? KpiTextForTracking(volume)[PerformanceType.Value]
        : KpiTextForTracking(volume)[PerformanceType.Volume],
      mainFilterState:
        getPageTypeWithPageName(retailerClientState.pageViewing) === PageType.RI_PRODUCT
          ? mainFiltersStateProducts
          : mainFiltersStateRO,
      hasData: !!data,
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(
        pagesConfig,
        getPageTypeWithPageName(retailerClientState.pageViewing),
        effectiveProductType,
      ),
    };
  };
  return { createParam };
};

export interface ShareComparisonChartProps {
  isLoading: boolean;
  data: ShareComparisonChartResp;
  isValue: boolean;
  selectedPeriodicity: Periodicity;
  factRevenue: FactRevenue;
  volume: string;
  setIsButtonDisabled?: Dispatch<SetStateAction<boolean>>;
  componentName?: string;
  retailerClientStateKey?: RetailerClientStateKeys;
  onExportReady?: OnExportReadyCallback;
  isUsingCollection?: boolean;
  isApiError?: boolean;
}

const ShareComparisonChart: React.FC<ShareComparisonChartProps> = ({
  isLoading,
  data,
  isValue,
  selectedPeriodicity,
  factRevenue,
  componentName = PERFORMANCE_TREND_TITLE,
  onExportReady,
  volume,
  isUsingCollection,
  setIsButtonDisabled,
  isApiError = false,
}) => {
  const { createParam } = useShareComparisonChartCreateViewTrackingParam(componentName, data, isValue, volume);

  const { ref: ShareComparisonCardComponent } = useInViewTracking(createParam);
  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        numericSymbols: ['k', 'm', 'b', 't', 'p', 'e'],
      },
    });
  }, []);

  const chartRef = useRef<Chart | null>(null);

  const timeOutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const options = getChartOptions(data, isValue, selectedPeriodicity, factRevenue, volume);

  const handleChartCallback = useCallback<ChartCallbackFunction>(
    (newChart: Chart) => {
      chartRef.current = newChart;
    },
    [chartRef],
  );

  const callback = useSkipExportRenderingCallback(handleChartCallback);

  useEffect(() => {
    timeOutRef.current = setTimeout(() => {
      if (chartRef.current?.options) {
        chartRef.current.reflow();
      }
    }, 1000);

    return () => {
      if (timeOutRef.current) {
        clearTimeout(timeOutRef.current);
      }
    };
  }, [chartRef, options]);

  const { setChartRefsIsInitialisedStatus } = useExportContext();

  useEffect(() => {
    // Set a status flag to indicate that the chart has been initialised. This is needed to ensure
    // that all the charts are ready for export.
    setChartRefsIsInitialisedStatus((prev) => {
      if (isEmpty(chartRef.current) || prev[0]) return prev;

      const newRefs = [...prev];
      newRefs[0] = true;
      return newRefs;
    });
  }, [chartRef.current]);

  useRegisterChartForExport(chartRef, onExportReady, EXPORT_CHART_SIZE, EXPORT_RENDERING_RESOLUTION, undefined, []);

  return renderVisualization({
    isLoading: isLoading,
    fetchedApiData: data,
    defaultData: new ShareComparisonChartResp(),
    jsxToRender: (
      <div ref={isUsingCollection ? null : ShareComparisonCardComponent}>
        <HighchartsReact callback={callback} highcharts={Highcharts} options={options} />
      </div>
    ),
    noResultHandler: () => setIsButtonDisabled?.(true),
    noResultJsx: <div ref={isUsingCollection ? null : ShareComparisonCardComponent}>{NoResult()}</div>,
    errorMessageJsx: <div ref={isUsingCollection ? null : ShareComparisonCardComponent}>{ErrorResultMessage()}</div>,
    isError: isApiError,
  });
};

export default ShareComparisonChart;
